/** Demo Mode only - renders finance exception cards (see financeExceptions.ts). Each card
 * states the issue, affected scope, financial impact and links to the relevant detail view. */

import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FinanceExceptionCard } from '../financeExceptions';

const SEVERITY_TO_ALERT: Record<FinanceExceptionCard['severity'], 'info' | 'warning' | 'error'> = {
  Info: 'info',
  Warning: 'warning',
  Blocking: 'error',
};

export interface FinanceExceptionCardsProps {
  exceptions: FinanceExceptionCard[];
  emptyMessage?: string;
}

const FinanceExceptionCards = ({ exceptions, emptyMessage }: FinanceExceptionCardsProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  if (exceptions.length === 0) {
    return emptyMessage ? (
      <Typography variant="textSM"
        color="text.secondary">{emptyMessage}</Typography>
    ) : null;
  }

  return (
    <Stack spacing={1.5}>
      {exceptions.map((exception) => (
        <Alert key={exception.id}
          severity={SEVERITY_TO_ALERT[exception.severity]}
          variant="outlined">
          <AlertTitle>{exception.title}</AlertTitle>
          <Typography variant="textSM">{exception.message}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="textXS"
              color="text.secondary">
              {`${exception.scopeLabel} \u2022 ${t('financeExceptionImpact', 'Impact')}: ${exception.impact}`}
            </Typography>
            <Button variant="secondary"
              size="md"
              onClick={() => navigate(exception.linkTo)}>
              {t('financeExceptionViewDetail', 'View detail')}
            </Button>
          </Box>
        </Alert>
      ))}
    </Stack>
  );
};

export default FinanceExceptionCards;
